import { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock } from 'lucide-react';

const MESICE_CZ = [
    'leden', 'únor', 'březen', 'duben', 'květen', 'červen',
    'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec',
];
const DNY_KRATKE = ['po', 'út', 'st', 'čt', 'pá', 'so', 'ne'];

const fmtKc = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');
const fmtH = (n) => (n == null ? '—' : `${Number(n).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} h`);
const fmtKcSigned = (n) => {
    if (n == null) return '—';
    const v = Math.round(Number(n));
    if (v === 0) return '0 Kč';
    return (v > 0 ? '+' : '−') + Math.abs(v).toLocaleString('cs-CZ') + ' Kč';
};

/**
 * Mini měsíční přehled — pod kartou nadcházející směny ve stavu `pre`.
 * Mřížka 6×7 (po-ne), den s plánovanou směnou má barevnou tečku.
 * Pod kalendářem progress bar aktuálního výdělku vs plánu.
 */
export default function MonthOverview({ isDark, fill = false, flat = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const dnesObj = useMemo(() => new Date(), []);
    const rok = dnesObj.getFullYear();
    const mesic = dnesObj.getMonth() + 1;

    useEffect(() => {
        setLoading(true);
        fetch(`/api/me/mesic?rok=${rok}&mesic=${mesic}`)
            .then(r => r.ok ? r.json() : null)
            .then(setData)
            .finally(() => setLoading(false));
    }, [rok, mesic]);

    const dnyKalendare = useMemo(() => {
        const prvni = new Date(rok, mesic - 1, 1);
        const posledni = new Date(rok, mesic, 0);
        const offsetPondeli = (prvni.getDay() + 6) % 7; // pondělí jako první den
        const buffer = [];
        for (let i = 0; i < offsetPondeli; i++) buffer.push(null);
        for (let d = 1; d <= posledni.getDate(); d++) {
            const datum = `${rok}-${String(mesic).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            buffer.push({ den: d, datum });
        }
        while (buffer.length % 7 !== 0) buffer.push(null);
        return buffer;
    }, [rok, mesic]);

    const dnySSmenou = useMemo(() => {
        if (!data?.dny) return new Map();
        return new Map(data.dny.map(d => [d.datum, d.smeny]));
    }, [data]);

    const dnesDatum = `${rok}-${String(mesic).padStart(2, '0')}-${String(dnesObj.getDate()).padStart(2, '0')}`;

    const cardBg = isDark ? '#1a1a1f' : '#ffffff';
    const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const labelMuted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const textMuted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const textPrimary = isDark ? '#fff' : 'var(--text-primary)';
    const dayBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';

    const aktualniKc = data?.vydelek?.aktualni_kc ?? 0;
    const planovanyKc = data?.vydelek?.planovany_kc ?? 0;
    const dyskoKc = data?.vydelek?.dysko_kc ?? 0;
    const procento = planovanyKc > 0 ? Math.min(100, Math.round((aktualniKc / planovanyKc) * 100)) : 0;
    const hodAkt = data?.hodiny?.aktualni ?? 0;
    const hodPlan = data?.hodiny?.planovane ?? 0;

    return (
        <div style={{
            // flat = plochý průhledný panel (mobil): pozadí drží mateřský prvek
            // v index.jsx (base + fázový gradient), tady nic — žádná hrana.
            // Na mobilu je to spodní panel cockpitu až u hrany displeje. Kalendář
            // není na klepnutí, takže smí do spodní safe-area — končí jen těsně
            // nad home indicatorem (safe-area − 10 px), bez safe-area 12 px.
            marginTop: flat ? 0 : 10,
            background: flat ? 'transparent' : cardBg,
            border: flat ? 'none' : `1px solid ${cardBorder}`,
            borderRadius: flat ? 0 : 16,
            padding: flat ? '12px 16px max(12px, calc(env(safe-area-inset-bottom, 0px) - 10px))' : 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            ...(fill ? { flex: 1, minHeight: 0 } : null),
        }}>
            {/* Header — ikona + měsíc + (vpravo) procento výdělku */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Calendar size={13} style={{ color: '#0CC0DF' }} />
                    <span style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                        textTransform: 'uppercase', color: labelMuted,
                    }}>{MESICE_CZ[mesic - 1]} {rok}</span>
                </span>
                {!loading && (planovanyKc > 0 || aktualniKc > 0) && (
                    <span style={{
                        fontSize: 11, fontWeight: 700, color: '#FF8A00',
                        fontVariantNumeric: 'tabular-nums',
                    }}>{procento} %</span>
                )}
            </div>

            {/* Záhlaví dnů */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2,
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                color: textMuted, textAlign: 'center',
            }}>
                {DNY_KRATKE.map(d => <span key={d}>{d}</span>)}
            </div>

            {/* Mřížka dnů — fill režim: roste s dostupným prostorem (flex 1, 6 stejných řádků).
                Nesplňuje-li, fallback na fixní 28 px buňky. */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2,
                ...(fill
                    ? { flex: 1, minHeight: 0, gridTemplateRows: 'repeat(6, minmax(28px, 1fr))' }
                    : null),
            }}>
                {dnyKalendare.map((b, i) => {
                    if (!b) return <span key={`e-${i}`} />;
                    const smeny = dnySSmenou.get(b.datum) || [];
                    const isToday = b.datum === dnesDatum;
                    const isPast = b.datum < dnesDatum;
                    const tecka = smeny[0]?.sablon_barva || '#0CC0DF';

                    return (
                        <div key={b.datum} style={{
                            position: 'relative',
                            ...(fill ? null : { height: 28 }),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderRadius: 5,
                            background: isToday ? 'rgba(255,138,0,0.18)' : (smeny.length ? dayBg : 'transparent'),
                            border: isToday ? '1px solid rgba(255,138,0,0.55)' : '1px solid transparent',
                            color: isPast && !smeny.length ? textMuted : textPrimary,
                            fontSize: 11, fontWeight: isToday ? 700 : 500,
                            fontVariantNumeric: 'tabular-nums',
                        }}>
                            <span>{b.den}</span>
                            {smeny.length > 0 && (
                                <span style={{
                                    position: 'absolute', bottom: 3,
                                    width: 4, height: 4, borderRadius: '50%',
                                    background: tecka,
                                    boxShadow: smeny.length > 1 ? `5px 0 0 ${tecka}` : 'none',
                                }} />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Výdělek — souhrnný panel: label + dýško, velké číslo vs cíl,
                progress bar, dole odpracované hodiny + procento. */}
            {!loading && (planovanyKc > 0 || aktualniKc > 0) && (
                <div style={{
                    marginTop: 4, paddingTop: 12,
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                    display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                    {/* Hlavička — label vlevo, dýško vpravo */}
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{
                            fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5,
                            textTransform: 'uppercase', color: labelMuted,
                        }}>Vyděláno tento měsíc</span>
                        {dyskoKc !== 0 && (
                            <span style={{
                                fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                color: dyskoKc >= 0 ? '#10B981' : '#EF4444',
                            }}>{fmtKcSigned(dyskoKc)} dýško</span>
                        )}
                    </div>

                    {/* Velké číslo + cíl */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                        <strong style={{
                            fontSize: 26, fontWeight: 800, color: textPrimary,
                            fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5, lineHeight: 1,
                        }}>
                            {Math.round(aktualniKc).toLocaleString('cs-CZ')}
                        </strong>
                        <span style={{ fontSize: 12, color: textMuted, fontVariantNumeric: 'tabular-nums' }}>
                            / {fmtKc(planovanyKc)} cíl
                        </span>
                    </div>

                    {/* Progress bar */}
                    <div style={{
                        height: 7, borderRadius: 3,
                        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            height: '100%', width: `${procento}%`,
                            background: 'linear-gradient(90deg, #FF8A00, #F59E0B)',
                            borderRadius: 3,
                            transition: 'width 0.3s ease',
                        }} />
                    </div>

                    {/* Dole — odpracované hodiny vlevo, procento vpravo */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            fontSize: 11, color: labelMuted, fontVariantNumeric: 'tabular-nums',
                        }}>
                            <Clock size={11} style={{ flexShrink: 0 }} />
                            {Number(hodAkt).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} / {fmtH(hodPlan)} odpracováno
                        </span>
                        <span style={{
                            fontSize: 12, fontWeight: 800, color: '#FF8A00',
                            fontVariantNumeric: 'tabular-nums',
                        }}>{procento} %</span>
                    </div>
                </div>
            )}
        </div>
    );
}
